/**
 * backdropSnapshot — derive a one-time-per-session blurred-backdrop image
 * from a video source so the viewer doesn't have to keep a second live
 * decoder running just to fill the letterbox bars.
 *
 * Design goals:
 *  - Never throw to the caller. Worst case → returns null and the caller
 *    falls back to brand_color.
 *  - Be Safari-safe: opt out automatically on CORS-tainted canvas, on tabs
 *    flagged low-memory, and on any unhandled error.
 *  - Cache results for the lifetime of the page session, keyed by URL.
 */

const cache = new Map(); // url → { status: 'ok'|'failed'|'pending', dataUrl?: string, promise?: Promise }

/** Heuristic: skip the snapshot path entirely on devices that report low memory. */
function isLowMemoryDevice() {
  try {
    const dm = navigator?.deviceMemory;
    if (typeof dm === "number" && dm > 0 && dm < 4) return true;
    // Safari/iOS doesn't expose deviceMemory. Use hardwareConcurrency as a weak proxy
    // (a 2-core device is almost certainly the iPad mini or similar).
    if ((navigator?.hardwareConcurrency || 8) <= 2) return true;
  } catch (_) {}
  return false;
}

/** Capture the already-visible frame; never create a second decoder/download. */
async function captureFrame(video) {
  try {
    const targetW = 240;
    const ratio = video.videoWidth / video.videoHeight;
    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = Math.round(targetW / Math.max(0.5, ratio));
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.72);
  } catch (_) {
    // A third-party video may taint canvas. Keep the brand-color fallback.
    return null;
  }
}

/**
 * Return a Promise<string|null> — the cached or freshly generated data URL.
 * `null` means: caller should fall back to brand_color.
 */
export function getOrGenerateBackdropSnapshot(videoUrl, sourceVideo) {
  if (!videoUrl) return Promise.resolve(null);
  if (isLowMemoryDevice()) {
    // Memory-pressure guard — don't risk a snapshot path that could be
    // heavier than just falling back to brand_color.
    return Promise.resolve(null);
  }
  const entry = cache.get(videoUrl);
  if (entry?.status === "ok") return Promise.resolve(entry.dataUrl);
  if (entry?.status === "failed") return Promise.resolve(null);
  if (entry?.promise) return entry.promise;

  if (!sourceVideo || sourceVideo.readyState < 2 || !sourceVideo.videoWidth || !sourceVideo.videoHeight) {
    return Promise.resolve(null);
  }
  const promise = captureFrame(sourceVideo).then((dataUrl) => {
    if (dataUrl) {
      cache.set(videoUrl, { status: "ok", dataUrl });
      return dataUrl;
    }
    cache.set(videoUrl, { status: "failed" });
    return null;
  }).catch(() => {
    cache.set(videoUrl, { status: "failed" });
    return null;
  });

  cache.set(videoUrl, { status: "pending", promise });
  return promise;
}

/** Synchronous cache lookup — returns the data URL if we already have one. */
export function peekBackdropSnapshot(videoUrl) {
  const entry = cache.get(videoUrl);
  return entry?.status === "ok" ? entry.dataUrl : null;
}

/** Synchronous read of the last snapshot attempt's outcome for debug labels. */
export function peekBackdropSnapshotStatus(videoUrl) {
  const entry = cache.get(videoUrl);
  if (!entry) return "idle";
  return entry.status; // 'pending' | 'ok' | 'failed'
}
